import { HeartHandshake, Gem, ShieldCheck } from "lucide-react";
import { motion } from "framer-motion";
import { DIFFERENTIATORS } from "@/data/content";
import { PageHero } from "@/components/site/PageHero";
import { CTABand } from "@/components/site/CTABand";
import { MaskLines, Reveal } from "@/components/site/Reveal";
import { Stats } from "@/components/home/Stats";
import { useSeo } from "@/hooks/useSeo";

const VALUES = [
  { icon: HeartHandshake, title: "Service", text: "We answer fast, explain clearly and own the outcome — every ticket, every visit." },
  { icon: Gem, title: "Quality", text: "Documented standards, certified engineers and photo-verified work. Done right the first time." },
  { icon: ShieldCheck, title: "Peace of mind", text: "Proactive monitoring and fair, predictable pricing, so problems — and bills — never surprise you." },
];

export default function WhoWeAre() {
  useSeo("Who We Are | Bodhatek", "Bodhatek's mission, values and what makes us different: service, quality and peace of mind.");
  return (
    <div data-testid="who-we-are-page">
      <PageHero
        testId="who-hero"
        eyebrow="Who we are"
        lines={["Technology that feels", { text: "handled.", className: "text-grad" }]}
        sub="Bodhatek unites managed IT, dispatch and nationwide onsite support under one roof, at a very reasonable cost — so the systems and the people behind them work as one."
      />
      <section data-testid="mission-section" className="bg-[#FAFCFF] py-24 sm:py-32">
        <div className="container-x grid items-center gap-14 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <p className="eyebrow text-[#1777AF]">Our mission</p>
            <MaskLines lines={["To give every business", "the operational calm", "of an enterprise."]} className="mt-5 font-display text-4xl font-bold leading-[1.04] tracking-tight text-slate-900 sm:text-5xl" />
            <Reveal delay={0.15}>
              <p className="mt-6 max-w-lg leading-relaxed text-slate-600">
                [Placeholder] Founded to close the gap between IT providers and field-service vendors, Bodhatek gives growing organizations one affordable partner that supports, dispatches and delivers — anywhere in the USA.
              </p>
            </Reveal>
          </div>
          <Reveal className="lg:col-span-6">
            <div className="clip-notch aspect-[4/3] overflow-hidden rounded-3xl">
              <img src="/images/team.webp" alt="Bodhatek team planning client support" loading="lazy" className="h-full w-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>
      <section data-testid="values-section" className="bg-[#EAF0F7] py-24 sm:py-32">
        <div className="container-x">
          <p className="eyebrow text-[#1777AF]">Our values</p>
          <div className="mt-12 grid gap-4 md:grid-cols-3">
            {VALUES.map((v, i) => (
              <motion.div
                key={v.title}
                data-testid={`value-card-${i}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.8, delay: i * 0.1 }}
                className="group rounded-3xl border border-slate-200 bg-white p-10 transition-[transform,box-shadow] duration-500 hover:-translate-y-1.5 hover:shadow-[0_24px_60px_-30px_rgba(23,119,175,0.45)]"
              >
                <span className="grid h-14 w-14 place-items-center rounded-2xl bg-gradient-to-br from-[#1777AF] to-[#29C1D3] text-white transition-transform duration-500 group-hover:rotate-6"><v.icon className="h-6 w-6" /></span>
                <h3 className="mt-8 font-display text-3xl font-semibold tracking-tight text-slate-900">{v.title}</h3>
                <p className="mt-3 leading-relaxed text-slate-600">{v.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      <section data-testid="differentiators-section" className="bg-white py-24 sm:py-32">
        <div className="container-x">
          <p className="eyebrow text-[#1777AF]">What makes us different</p>
          <div className="mt-12 grid gap-px overflow-hidden rounded-3xl border border-slate-200 bg-slate-200 sm:grid-cols-2">
            {DIFFERENTIATORS.map((d) => (
              <Reveal key={d.n} className="bg-white p-10">
                <span className="font-mono text-sm text-[#1777AF]">{d.n}</span>
                <h3 className="mt-4 font-display text-2xl font-semibold tracking-tight text-slate-900">{d.title}</h3>
                <p className="mt-3 leading-relaxed text-slate-600">{d.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <Stats />
      <CTABand />
    </div>
  );
}
